import { api } from "../api";
import { currentDownloadSurface } from "./downloadSurface";
import { ptDlDo } from "./download";
import { toastError, toastSuccess } from "../ui/messages";
import { getDomElement, setHtml, fadeIn, fadeOut, hide } from "../utils/dom";
import { ID_DOWNLOAD_CART_COUNT } from "../utils/elementIds";
import { mountDownloadCart } from "./components/DownloadCart";
import { updateDownloadStatus } from "./cart";
import {
  getDownloadState,
  startBrowserDownload as startBrowserDownloadJob,
} from "./browserDownload/controller";
import { showBrowserDownloaderNotice } from "./browserDownload/notice";

function updateCartCount(delta: number): void {
  const countEl = getDomElement(ID_DOWNLOAD_CART_COUNT);
  if (!countEl) throw new Error("Element not found: #download-cart-count");
  let count = parseInt(countEl.innerHTML ?? "0") + delta;

  if (count <= 0) {
    count = 0;
    fadeOut("download-cart");
    fadeOut("download-cart-button");
    hide("download-cart-back");
  } else {
    fadeIn("download-cart-button");
  }
  countEl.innerHTML = String(count);
}

/**
 * Ids with a cart toggle in flight.
 *
 * Two fast clicks used to fire two toggles: the artwork was added and then immediately
 * removed, while `updateCartCount` was applied twice in the same direction — so the badge
 * drifted out of step with the actual cart. The guard is correctness, not just polish.
 */
const pendingCartToggles = new Set<string>();

/**
 * Show that a cart toggle is under way, and return a function that restores the button.
 *
 * The toggle is a server-side decision — the client does not know whether the click adds or
 * removes until the response arrives — so this reports "working" rather than guessing the
 * outcome. It is deliberately not an optimistic update: guessing the direction would mean
 * reading the button's own markup back, and the honest spinner costs nothing once the
 * round trips are down to three.
 */
function markCartButtonsBusy(id: string): () => void {
  const ids = [`overlay-dlcart${id}`, `view-dlcart-${id}`, `view-download-buttons-${id}`];
  const previous = new Map<string, string>();
  for (const elId of ids) {
    const el = getDomElement(elId);
    if (!el) continue;
    previous.set(elId, el.innerHTML);
    el.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Working…';
    el.classList.add("cart-busy");
  }
  return () => {
    for (const [elId, html] of previous) {
      const el = getDomElement(elId);
      if (!el) continue;
      // Only roll back if nothing else has already written a final label here.
      if (el.classList.contains("cart-busy")) el.innerHTML = html;
      el.classList.remove("cart-busy");
    }
  };
}

export async function addDownloadCart(id: string): Promise<void> {
  if (pendingCartToggles.has(id)) return;
  pendingCartToggles.add(id);
  const restore = markCartButtonsBusy(id);

  let response: { added: boolean };
  try {
    response = await api.downloadCart.toggle({ id: Number(id), ...currentDownloadSurface(id) });
  } catch (err: unknown) {
    restore();
    alert((err as Error).message);
    return;
  } finally {
    pendingCartToggles.delete(id);
  }
  restore();

  if (response.added) {
    toastSuccess("Added artwork to downloader");
    setHtml("overlay-dlcart" + id, '<i class="fa fa-download"></i> Remove');
    setHtml("view-dlcart-" + id, '<i class="fas fa-download"></i> Remove');
    updateCartCount(1);
    void refreshDownloadCartIfVisible();
  } else {
    toastSuccess("Removed artwork from downloader");
    setHtml("overlay-dlcart" + id, '<i class="fa fa-download"></i> Download');
    setHtml("view-dlcart-" + id, '<i class="fas fa-download"></i> Download');
    restoreDownloadButtons(id);
    updateCartCount(-1);
    void refreshDownloadCartIfVisible();
  }
}

function restoreDownloadButtons(id: string): void {
  const container = getDomElement(`view-download-buttons-${id}`);
  if (!container) return;
  container.innerHTML =
    '<span class="view-download-label">DOWNLOAD</span> ' +
    `<span class="button small yellow dlbuttonfix add-cart-btn"><i class="fas fa-plus"></i> Later</span> &nbsp; ` +
    `<span class="buttom small green dlbuttonfix quick-dl-btn" id="overlay-dlcart2${id}"><i class="fas fa-download"></i> Quick</span>`;
  bindDownloadBtns(id);
}

function inDlcHtml(id: string): string {
  return (
    '<span style="font-size: 0.7em; font-weight: 400;"><i>Artwork added to download cart</i></span> &nbsp; ' +
    `<span class="button small dlbuttonfix remove-cart-btn" style="background:#c0392b;color:#fff;font-size:0.7em;" data-id="${id}"><i class="fas fa-times"></i> Remove</span>`
  );
}

export async function addDownloadCart2(id: string): Promise<void> {
  if (pendingCartToggles.has(id)) return;
  pendingCartToggles.add(id);
  const restore = markCartButtonsBusy(id);

  let response: { added: boolean };
  try {
    response = await api.downloadCart.toggle({ id: Number(id), ...currentDownloadSurface(id) });
  } catch (err: unknown) {
    restore();
    alert((err as Error).message);
    return;
  } finally {
    pendingCartToggles.delete(id);
  }
  restore();

  if (response.added) {
    toastSuccess("Added artwork to downloader");
    setHtml("view-download-buttons-" + id, inDlcHtml(id));
    bindRemoveCartBtn(id);
    updateCartCount(1);
    void refreshDownloadCartIfVisible();
  } else {
    toastSuccess("Removed artwork from downloader");
    setHtml("overlay-dlcart" + id, '<i class="fa fa-download"></i> Download');
    setHtml("view-dlcart-" + id, '<i class="fas fa-download"></i> Download');
    updateCartCount(-1);
    void refreshDownloadCartIfVisible();
  }
}

export async function removeFromDownloadCart2(id: string): Promise<void> {
  if (pendingCartToggles.has(id)) return;
  pendingCartToggles.add(id);
  const restore = markCartButtonsBusy(id);

  let response: { added: boolean };
  try {
    response = await api.downloadCart.toggle({ id: Number(id), ...currentDownloadSurface(id) });
  } catch (err: unknown) {
    restore();
    alert((err as Error).message);
    return;
  } finally {
    pendingCartToggles.delete(id);
  }
  restore();

  if (!response.added) {
    toastSuccess("Removed artwork from downloader");
    restoreDownloadButtons(id);
    updateCartCount(-1);
    void refreshDownloadCartIfVisible();
  }
}

function bindRemoveCartBtn(id: string): void {
  const container = getDomElement(`view-download-buttons-${id}`);
  container?.querySelector(".remove-cart-btn")?.addEventListener("click", (e) => {
    e.stopPropagation();
    void removeFromDownloadCart2(id);
  });
}

function bindDownloadBtns(id: string): void {
  const container = getDomElement(`view-download-buttons-${id}`);
  container?.querySelector(".add-cart-btn")?.addEventListener("click", (e) => {
    e.stopPropagation();
    void addDownloadCart2(id);
  });
  container?.querySelector(".quick-dl-btn")?.addEventListener("click", (e) => {
    e.stopPropagation();
    void ptDlDo(id);
  });
}

async function refreshDownloadCartIfVisible(): Promise<void> {
  if (getDomElement("download-cart")?.style.display !== "block") return;
  try {
    const response = await api.downloadCart.getCart();
    mountDownloadCart(response);
  } catch {
    // ignore
  }
}

export async function download(id: string): Promise<void> {
  const artworkIds = id
    .split(",")
    .map(Number)
    .filter((n) => n > 0);

  /**
   * Set when the member asked for marketing material and we then fall back to the classic
   * downloader, which does not build that zip.
   *
   * ⚠️ Said out loud rather than dropped quietly. The cart only offers the toggle to
   * members on the enhanced downloader, so the only way to reach the classic path with it
   * on is this fallback — a path nobody chose and nothing on screen would otherwise
   * explain. A reseller who unzips and finds no marketing folder would reasonably conclude
   * the artists have none.
   */
  let marketingWanted = false;

  // Which downloader this member gets is decided by the server and reported by getCart, so
  // the choice is made before anything is started rather than by catching a FORBIDDEN.
  // Everyone not explicitly opted in falls through to the Lambda path below, unchanged.
  try {
    const cart = await api.downloadCart.getCart();
    if (cart.state === "cart") marketingWanted = cart.settings?.marketing ?? false;
    if (cart.state === "cart" && cart.browserDownloader) {
      // First browser download for this member: tell them the tab has to stay open, which is
      // the one thing the Lambda downloader never asked of them. Awaited, so the download
      // begins the moment they dismiss it — the notice is a beat in the flow, not a detour.
      await showBrowserDownloaderNotice(cart.browserDownloaderNoticeSeen);

      await startBrowserDownloadJob(artworkIds);

      // Did it actually finish? An aborted or partly-failed job leaves the cart alone so the
      // reseller can resume it; only a complete one clears up.
      const s = getDownloadState();
      const finished = s.artworksTotal > 0 && s.artworksDone >= s.artworksTotal;

      const refreshed = await api.downloadCart.getCart();
      mountDownloadCart(refreshed);

      // The server empties the cart rows on completion; this is the chrome half — the badge
      // and the panel live outside the cart component, so a re-render does not touch them.
      // Mirrors what the Lambda path does from its own finished-download button.
      if (finished) {
        const countEl = getDomElement(ID_DOWNLOAD_CART_COUNT);
        if (countEl) countEl.innerHTML = "0";
        fadeOut("download-cart");
        fadeOut("download-cart-button");
        hide("download-cart-back");
      }
      return;
    }
  } catch {
    // Fall through to the Lambda path: a failed capability check must not block a download.
  }

  try {
    await api.downloadCart.download({
      id: artworkIds,
    });
    toastSuccess("Your download is being prepared. Please wait.");
    if (marketingWanted) {
      toastError(
        "Marketing material is not included: this download fell back to the classic downloader."
      );
    }

    const response2 = await api.downloadCart.getCart();
    mountDownloadCart(response2);

    if (response2.state === "downloading") {
      void updateDownloadStatus();
    }
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export { bindRemoveCartBtn };
